import { Injectable } from '@angular/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import * as saveAs from 'file-saver';
import * as Sentry from '@sentry/angular';

interface CordovaPlugins {
  fileOpener2: {
    open: (
      path: string,
      mimeType: string,
      callbacks: { error: (err: Error) => void; success: () => void }
    ) => void;
  };
}

interface CordovaInterface {
  plugins: CordovaPlugins;
}

declare let cordova: CordovaInterface;

@Injectable({
  providedIn: 'root'
})
export class FilesHelperService {
  /**
   * Opens a file with the appropriate application based on its MIME type
   * @param filePath Path to the file in the device's file system
   * @param fileMIMEType MIME type of the file
   */
  public async openFile(filePath: string, fileMIMEType: string): Promise<void> {
    try {
      const fullFilePath = await Filesystem.getUri({
        directory: Directory.Documents,
        path: filePath
      });

      if (typeof cordova !== 'undefined' && cordova.plugins?.fileOpener2) {
        await new Promise<void>((resolve, reject) => {
          cordova.plugins.fileOpener2.open(fullFilePath.uri, fileMIMEType, {
            error: (err: Error) => {
              Sentry.captureException(err);
              reject(new Error('Не удалось открыть файл'));
            },
            success: () => resolve()
          });
        });
      } else {
        window.open(fullFilePath.uri, '_blank');
      }
    } catch (error) {
      Sentry.captureException(error);
      throw new Error('Не удалось открыть файл');
    }
  }

  /**
   * Shares a file using the device's native sharing capabilities or downloads it on web
   * @param filePath Path to the file in the device's file system
   * @param fileName Name of the file
   */
  public async shareFile(filePath: string, fileName: string): Promise<void> {
    try {
      const fullFilePath = await Filesystem.getUri({
        directory: Directory.Documents,
        path: filePath
      });

      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: fileName,
          url: fullFilePath.uri,
          dialogTitle: 'Поделиться файлом'
        });
      } else {
        const fileContent = await Filesystem.readFile({
          directory: Directory.Documents,
          path: filePath
        });

        const blob = this.base64ToBlob(
          fileContent.data as string,
          'application/octet-stream'
        );
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      Sentry.captureException(error);
      throw new Error('Не удалось поделиться файлом');
    }
  }

  /**
   * Converts a base64 string to a Blob
   * @param base64 Base64 string to convert
   * @param contentType MIME type of the content
   * @returns Blob object
   */
  public base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  /**
   * Get MIME type from file extension
   * @param extension File extension (e.g., 'pdf', 'jpg', etc.)
   * @returns MIME type string
   */
  public getMimeTypeFromExtension(extension: string): string {
    switch (extension.toLowerCase()) {
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'pdf':
        return 'application/pdf';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      default:
        return 'application/octet-stream';
    }
  }

  /**
   * Get MIME type from file content by examining the file header
   * @param fileBlob Blob object containing the file data
   * @param fileName Optional file name to determine MIME type from extension
   * @returns Promise resolving to the MIME type string
   */
  public async getMimeTypeFromContent(
    fileBlob: Blob,
    fileName?: string
  ): Promise<string> {
    if (fileBlob.type) {
      return fileBlob.type;
    }

    if (fileName) {
      const extension = fileName.toLowerCase().split('.').pop() || '';
      const mimeType = this.getMimeTypeFromExtension(extension);
      if (mimeType !== 'application/octet-stream') {
        return mimeType;
      }
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const arr = new Uint8Array(reader.result as ArrayBuffer).subarray(0, 4);
        const header = Array.from(arr)
          .map(byte => byte.toString(16).padStart(2, '0'))
          .join('');

        switch (header) {
          case '89504e47':
            resolve('image/png');
            break;
          case '47494638':
            resolve('image/gif');
            break;
          case '25504446':
            resolve('application/pdf');
            break;
          case 'ffd8ffe0':
          case 'ffd8ffe1':
          case 'ffd8ffe2':
          case 'ffd8ffe3':
          case 'ffd8ffe8':
            resolve('image/jpeg');
            break;
          case '504b0304':
            resolve('application/zip');
            break;
          default:
            resolve('application/octet-stream');
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(fileBlob);
    });
  }

  /**
   * Sanitizes a file name by replacing invalid characters
   * @param fileName File name to sanitize
   * @returns Sanitized file name
   */
  public sanitizeFileName(fileName: string): string {
    return fileName.replace(/[/\\?%*:|"<>]/g, '-');
  }

  /**
   * Converts a Blob to a base64 string
   * @param blob Blob to convert
   * @returns Promise resolving to the base64 string
   */
  /**
   * Сохраняет blob файлом и открывает его. В web — обычное скачивание браузером. На iOS/Android —
   * запись в Documents и НЕМЕДЛЕННОЕ открытие: прежде компоненты писали файл в песочницу без
   * открытия/уведомления, и пользователю казалось, что кнопка не работает.
   *
   * @param blob Содержимое файла.
   * @param fileName Имя файла (в Documents на нативе / имя загрузки в web).
   * @param fileMIMEType MIME-тип для открытия нативным приложением.
   */
  public async saveAndOpenFile(
    blob: Blob,
    fileName: string,
    fileMIMEType: string
  ): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      saveAs(blob, fileName);
      return;
    }
    const base64Data = await this.convertBlobToBase64(blob);
    await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Documents,
      recursive: true
    });
    await this.openFile(fileName, fileMIMEType);
  }

  public async convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
    });
  }
}
